// src/follower/components/AccountStatusBar.jsx
// Phase 1 — reads from mockPrefs / mockStatus. No backend dependency.
// Section A of myTrades page.

import React, { useState, useEffect } from "react";

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
    bg:     "#08101e",
    bg1:    "#0c1525",
    bg2:    "#101b2e",
    border: "#1e2d42",
    text:   "#e8edf5",
    body:   "#b8c8dc",
    meta:   "#6a7d95",
    green:  "#00e896",
    cyan:   "#00c8ff",
    amber:  "#ffb340",
    red:    "#ff4466",
};
const FONT = "'DM Mono', monospace";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isMarketOpen() {
    const now = new Date();
    const et  = new Date(now.toLocaleString("en-US", { timeZone: "America/New_York" }));
    const day = et.getDay();                          // 0=Sun 6=Sat
    const mins = et.getHours() * 60 + et.getMinutes();
    return day >= 1 && day <= 5 && mins >= 570 && mins < 960; // 9:30–16:00 ET
}

function deriveStatus(prefs) {
    if (!prefs.is_active) return "INACTIVE";
    if (prefs.is_paused)  return "PAUSED";
    return "ACTIVE";
}

function pauseReasonLabel(reason) {
    if (!reason) return null;
    switch (reason) {
        case "MAX_LOSS_BREACHED":  return "Max loss reached";
        case "MAX_PROFIT_REACHED": return "Max profit reached";
        case "MANUAL":             return "Manually paused";
        default:                   return reason;
    }
}

function formatPnL(val) {
    if (val === null || val === undefined) return "--";
    const abs = Math.abs(val).toFixed(2);
    return val >= 0 ? `+$${abs}` : `-$${abs}`;
}

// ─── Small building blocks ────────────────────────────────────────────────────

const Dot = ({ color }) => (
    <div style={{
    width: 6, height: 6, borderRadius: "50%",
    background: color, flexShrink: 0,
    boxShadow: `0 0 6px ${color}`,
  }} />
);

const Chip = ({ label, color, bg }) => (
    <div style={{
    display:       "inline-flex",
    alignItems:    "center",
    padding:       "2px 8px",
    borderRadius:  3,
    border:        `1px solid ${color}30`,
    background:    bg || color + "12",
    fontFamily:    FONT,
    fontSize:      9,
    fontWeight:    500,
    letterSpacing: "1px",
    color,
  }}>
        {label}
    </div>
);

const Divider = () => (
    <div style={{ width: 1, height: 28, background: C.border, flexShrink: 0 }} />
);

const StatBlock = ({ label, value, valueColor }) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
    <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase" }}>
      {label}
    </span>
    <span style={{ fontFamily: FONT, fontSize: 13, fontWeight: 500, color: valueColor || C.text, letterSpacing: ".3px" }}>
      {value}
    </span>
    </div>
);

// ─── Main component ───────────────────────────────────────────────────────────
//
// Props:
//   prefs      — object from mockPrefs / GET /follower/prefs
//   dailyPnL   — number from mockStatus.daily_pnl / GET /follower/status
//   openCount  — number (open positions count)

export default function AccountStatusBar({ prefs, dailyPnL, openCount, lastSyncAt, onEditBroker, onToggleMode }) {
    const marketOpen = isMarketOpen();
    const status     = deriveStatus(prefs);
    const pauseLabel = pauseReasonLabel(prefs.pause_reason);

    // Tick every 30s so "Last sync" age stays fresh
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick(t => t + 1), 30000);
        return () => clearInterval(id);
    }, []);

    function lastSyncLabel() {
        if (!lastSyncAt) return "--";
        const age = Math.floor((Date.now() - lastSyncAt) / 60000);
        return age < 1 ? "just now" : `${age}m ago`;
    }

    // Status chip config
    const statusConfig = {
        ACTIVE:   { color: C.green, label: "ACTIVE" },
        PAUSED:   { color: C.amber, label: "PAUSED" },
        INACTIVE: { color: C.meta,  label: "INACTIVE" },
    }[status];

    // Mode chip config
    const modeConfig = prefs.trading_mode === "live"
        ? { color: C.green, label: "LIVE" }
        : { color: C.amber, label: "PAPER" };

    // P&L color
    const pnlColor = dailyPnL > 0 ? C.green : dailyPnL < 0 ? C.red : C.meta;

    return (
        <>
        <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&display=swap');
      `}</style>

        <div style={{
        background:   C.bg1,
        border:       `1px solid ${C.border}`,
        borderRadius: 10,
        padding:      "14px 24px",
        display:      "flex",
        alignItems:   "center",
        gap:          20,
        flexWrap:     "wrap",
      }}>

            {/* ── Broker label ── */}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Dot color={statusConfig.color} />
                <span style={{
                    fontFamily:    FONT,
                    fontSize:      11,
                    fontWeight:    500,
                    color:         C.text,
                    letterSpacing: ".5px",
                    textTransform: "uppercase",
                }}>
                    {prefs.broker}
                </span>
                {onEditBroker && (
                <button
                    onClick={onEditBroker}
                    style={{
                            fontFamily:    FONT,
                            fontSize:      8,
                            letterSpacing: ".8px",
                            color:         C.meta,
                            background:    "transparent",
                            border:        `1px solid ${C.border}`,
                            borderRadius:  3,
                            padding:       "2px 7px",
                            cursor:        "pointer",
                            transition:    "color .15s, border-color .15s",
                        }}
                    onMouseEnter={e => { e.target.style.color = C.cyan; e.target.style.borderColor = C.cyan + "50"; }}
                    onMouseLeave={e => { e.target.style.color = C.meta; e.target.style.borderColor = C.border; }}
                >
                    EDIT
                </button>
                    )}
            </div>

            <Divider />

            {/* ── Mode badge ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase" }}>
                    Mode
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Chip label={modeConfig.label} color={modeConfig.color} />
                    {onToggleMode && (
                    <button
                        onClick={onToggleMode}
                        style={{
                                fontFamily:    FONT,
                                fontSize:      8,
                                letterSpacing: ".8px",
                                color:         C.meta,
                                background:    "transparent",
                                border:        `1px solid ${C.border}`,
                                borderRadius:  3,
                                padding:       "2px 7px",
                                cursor:        "pointer",
                                transition:    "color .15s, border-color .15s",
                            }}
                        onMouseEnter={e => { e.target.style.color = modeConfig.color; e.target.style.borderColor = modeConfig.color + "50"; }}
                        onMouseLeave={e => { e.target.style.color = C.meta; e.target.style.borderColor = C.border; }}
                    >
                        SWITCH
                    </button>
                        )}
                </div>
            </div>

            <Divider />

            {/* ── Status badge + pause reason ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase" }}>
            Status
          </span>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Chip label={statusConfig.label} color={statusConfig.color} />
                    {status === "PAUSED" && pauseLabel && (
                    <span style={{ fontFamily: FONT, fontSize: 9, color: C.amber, letterSpacing: ".3px" }}>
                — {pauseLabel}
              </span>
                        )}
                </div>
            </div>

            <Divider />

            {/* ── Today P&L ── */}
            <StatBlock
                label="Today P&L"
                value={formatPnL(dailyPnL)}
                valueColor={pnlColor}
            />

            <Divider />

            {/* ── Open positions ── */}
            <StatBlock
                label="Open Positions"
                value={openCount ?? 0}
                valueColor={openCount > 0 ? C.cyan : C.meta}
            />

            {/* ── Last sync ── */}
            <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                <span style={{ fontFamily: FONT, fontSize: 8, color: C.meta, letterSpacing: "1px", textTransform: "uppercase" }}>Last Sync</span>
                <span style={{ fontFamily: FONT, fontSize: 11, color: lastSyncAt ? C.cyan : C.meta, letterSpacing: ".3px" }}>
                    {lastSyncLabel()}
                </span>
            </div>

            <Divider />

            {/* ── Market status — pushed to far right ── */}
            <div style={{ marginLeft: "auto" }}>
                {marketOpen ? (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <Dot color={C.green} />
              <span style={{ fontFamily: FONT, fontSize: 9, color: C.green, letterSpacing: ".5px" }}>
                MARKET OPEN
              </span>
                </div>
                    ) : (
                <div style={{
              display:      "flex",
              alignItems:   "center",
              gap:          8,
              padding:      "5px 12px",
              borderRadius: 4,
              border:       `1px solid ${C.border}`,
              background:   C.bg,
            }}>
                    <Dot color={C.meta} />
              <span style={{ fontFamily: FONT, fontSize: 9, color: C.meta, letterSpacing: ".5px" }}>
                MARKET CLOSED
              </span>
                </div>
                    )}
            </div>

        </div>
        </>
    );
}